import { useMemo } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";

// Recursos + labels — deve espelhar PERM_RESOURCES no backend
export const PERM_RESOURCES = [
  { k: "dashboard", l: "Dashboard (tela inicial)" },
  { k: "entregas", l: "Entregas" },
  { k: "kanban", l: "Kanban (quadro visual)", editOnly: true },
  { k: "financeiro", l: "Ver Valores / Faturamento / Ticket Médio", vOnly: true },
  { k: "acertos", l: "Acerto com Entregador (V=ver/exportar · I=registrar · A=editar · E=desfazer)" },
  { k: "logistica", l: "Logística" },
  { k: "monitoramento", l: "Monitoramento em Tempo Real", vOnly: true },
  { k: "bairros", l: "Bairros" },
  { k: "rotas", l: "Rotas (agrupamento de bairros)" },
  { k: "regioes", l: "Regiões" },
  { k: "tipos_entrega", l: "Tipos de Entrega" },
  { k: "formas_pgto", l: "Formas de Pagamento" },
  { k: "entregadores", l: "Entregadores" },
  { k: "users", l: "Usuários" },
  { k: "empresas", l: "Gerenciar Configurações da Empresa", editOnly: true },
  { k: "whatsapp_config", l: "Gerenciar WhatsApp (Z-API)", editOnly: true },
  { k: "whatsapp_logs", l: "Log de Mensagens WhatsApp", editOnly: true },
  { k: "relatorios", l: "Relatórios" },
  { k: "backup", l: "Backup/Restore" },
  { k: "auditoria", l: "Auditoria" },
];
const ACTIONS = [
  { k: "v", l: "V", title: "Visualizar" },
  { k: "i", l: "I", title: "Incluir" },
  { k: "a", l: "A", title: "Alterar" },
  { k: "e", l: "E", title: "Excluir" },
];

const emptyMatrix = () => Object.fromEntries(PERM_RESOURCES.map((r) => [r.k, { v: false, i: false, a: false, e: false }]));
const fullMatrix = () => Object.fromEntries(PERM_RESOURCES.map((r) => [r.k, { v: true, i: true, a: true, e: true }]));
const readonlyMatrix = () => Object.fromEntries(PERM_RESOURCES.map((r) => [r.k, { v: true, i: false, a: false, e: false }]));

export function PermsMatrix({ value, onChange, testIdPrefix = "matrix" }) {
  const matrix = useMemo(() => ({ ...emptyMatrix(), ...(value || {}) }), [value]);

  const toggle = (res, act) => {
    const next = { ...matrix, [res]: { ...matrix[res], [act]: !matrix[res][act] } };
    // Regra automática: se marca I/A/E, marca V também (não faz sentido incluir sem visualizar)
    if (act !== "v" && next[res][act]) next[res].v = true;
    onChange(next);
  };

  const toggleCol = (act) => {
    const allOn = PERM_RESOURCES.every((r) => matrix[r.k]?.[act]);
    const next = { ...matrix };
    for (const r of PERM_RESOURCES) {
      next[r.k] = { ...next[r.k], [act]: !allOn };
      if (!allOn && act !== "v") next[r.k].v = true;
    }
    onChange(next);
  };

  const toggleRow = (res) => {
    const allOn = ACTIONS.every((a) => matrix[res]?.[a.k]);
    onChange({ ...matrix, [res]: { v: !allOn, i: !allOn, a: !allOn, e: !allOn } });
  };

  return (
    <div className="border border-orange-200 rounded overflow-hidden">
      <div className="bg-orange-50 border-b border-orange-200 px-3 py-2 flex justify-between items-center flex-wrap gap-2">
        <div className="text-sm font-semibold">Matriz de Permissões · <span className="font-normal text-xs">V=Visualizar · I=Incluir · A=Alterar · E=Excluir</span></div>
        <div className="flex gap-1">
          <Button type="button" size="sm" variant="outline" onClick={() => onChange(readonlyMatrix())} className="text-xs h-6" data-testid={`${testIdPrefix}-btn-readonly`}>Só leitura</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onChange(fullMatrix())} className="text-xs h-6" data-testid={`${testIdPrefix}-btn-all`}>Tudo</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => onChange(emptyMatrix())} className="text-xs h-6" data-testid={`${testIdPrefix}-btn-none`}>Zerar</Button>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50">
            <TableHead className="w-56">Recurso</TableHead>
            {ACTIONS.map((a) => (
              <TableHead key={a.k} className="text-center cursor-pointer hover:bg-orange-100" onClick={() => toggleCol(a.k)}
                title={`${a.title} - clique para marcar/desmarcar coluna`}
                data-testid={`${testIdPrefix}-col-${a.k}`}>
                <div className="font-bold">{a.l}</div>
                <div className="text-[10px] font-normal">{a.title}</div>
              </TableHead>
            ))}
            <TableHead className="w-20 text-center text-xs">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {PERM_RESOURCES.map((r) => (
            <TableRow key={r.k}>
              <TableCell className="font-medium">{r.l}</TableCell>
              {ACTIONS.map((a) => {
                // Recursos "vOnly" (financeiro) só usam Visualizar.
                // Recurso "editOnly" (kanban, empresas) usa V + A (visualiza+altera; sem Incluir/Excluir).
                const disabled = (r.vOnly && a.k !== "v") || (r.editOnly && a.k !== "v" && a.k !== "a");
                return (
                  <TableCell key={a.k} className="text-center">
                    {disabled ? (
                      <span className="text-slate-300 text-xs">—</span>
                    ) : (
                      <input type="checkbox"
                        checked={!!matrix[r.k]?.[a.k]}
                        onChange={() => toggle(r.k, a.k)}
                        data-testid={`${testIdPrefix}-${r.k}-${a.k}`}
                        className="accent-orange-600 w-4 h-4" />
                    )}
                  </TableCell>
                );
              })}
              <TableCell className="text-center">
                <button type="button" onClick={() => toggleRow(r.k)}
                  className="text-[10px] text-orange-700 hover:underline"
                  data-testid={`${testIdPrefix}-row-${r.k}`}>
                  toggle
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Traduz um preset flat (lista de perms) para matriz aproximada
export function presetToMatrix(perms) {
  const m = emptyMatrix();
  const has = (k) => perms.includes(k);
  const setRes = (res, v = false, i = false, a = false, e = false) => { m[res] = { v, i, a, e }; };
  if (has("view_dashboard") || has("view_reports")) setRes("relatorios", true);
  if (has("view_logistica")) m.entregas.v = true;
  if (has("view_empresas")) setRes("empresas", true);
  if (has("view_audit")) setRes("auditoria", true);
  if (has("backup")) setRes("backup", true, true, true, true);
  if (has("manage_users")) setRes("users", true, true, true, true);
  if (has("manage_cadastros")) {
    for (const k of ["bairros","regioes","tipos_entrega","formas_pgto","entregadores"]) setRes(k, true, true, true, true);
  }
  if (has("cadastro_bairros")) setRes("bairros", true, true, true, true);
  if (has("cadastro_regioes")) setRes("regioes", true, true, true, true);
  if (has("cadastro_tipos_entrega")) setRes("tipos_entrega", true, true, true, true);
  if (has("cadastro_formas_pgto")) setRes("formas_pgto", true, true, true, true);
  if (has("cadastro_entregadores")) setRes("entregadores", true, true, true, true);
  if (has("create_entregas")) { m.entregas.v = true; m.entregas.i = true; }
  if (has("change_status")) { m.entregas.v = true; m.entregas.a = true; }
  if (has("delete_entregas")) { m.entregas.v = true; m.entregas.e = true; }
  return m;
}
